import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Switch from "react-switch";
import Swal from "sweetalert2";
import "./rules.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { useNavigate } from "react-router-dom";
import ColorPallet from "../color_pallet/colorPallet";
import { updateThemeData } from "../../functions/updateThemeData";
import { apiUrl } from "../../../utils/apiUrl";
function s3AssetUrl(base, filename) {
  if (!base || !filename) return "";
  return base + encodeURIComponent(String(filename)).replace(/%2F/g, "/");
}

const LANDING_TITLE_MAX_LEN = 15;
const YOGA_POSES_TITLE_MAX_LEN = 100;
const YOGA_UPLOAD_TITLE_MAX_LEN = 100;
const MIN_POINTS_PER_CHARM = 5;
const MAX_POINTS_PER_CHARM = 100;
const MIN_UPLOAD_COUNT = 1;
const MAX_UPLOAD_COUNT = 10;
const YOGA_SAMPLE_DIM = 200;

function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

async function validateYogaSampleFile(file, { allowedExt, label }) {
  const ext = file.name.split(".").pop().toLowerCase();
  if (!allowedExt.has(ext)) {
    Swal.fire(
      "Invalid file",
      `${label}: only ${[...allowedExt].join(", ")} allowed.`,
      "error"
    );
    return false;
  }
  if (file.size > 5 * 1024 * 1024) {
    Swal.fire("File too large", "Maximum 5 MB.", "error");
    return false;
  }
  try {
    const { width, height } = await getImageDimensions(file);
    if (width !== YOGA_SAMPLE_DIM || height !== YOGA_SAMPLE_DIM) {
      Swal.fire(
        "Invalid size",
        `${label} must be exactly ${YOGA_SAMPLE_DIM}×${YOGA_SAMPLE_DIM}px.`,
        "error"
      );
      return false;
    }
  } catch {
    Swal.fire("Error", "Could not read image dimensions.", "error");
    return false;
  }
  return true;
}

function clampPointsPerCharm(value) {
  const n = Number.parseInt(String(value ?? ""), 10);
  if (!Number.isFinite(n)) return MIN_POINTS_PER_CHARM;
  return Math.max(MIN_POINTS_PER_CHARM, Math.min(MAX_POINTS_PER_CHARM, n));
}

function clampUploadCount(value) {
  const n = Number.parseInt(String(value ?? ""), 10);
  if (!Number.isFinite(n)) return MIN_UPLOAD_COUNT;
  return Math.max(MIN_UPLOAD_COUNT, Math.min(MAX_UPLOAD_COUNT, n));
}

function parseStatusOpen(raw, defaultOpen = true) {
  const s = String(raw ?? (defaultOpen ? "open" : "closed")).trim().toLowerCase();
  return s !== "closed" && s !== "0" && s !== "false";
}

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((state) => state.theme);

  const [rules, setRules] = useState([""]);

  const [landingPageTitle, setLandingPageTitle] = useState("");
  const [thankYouPage, setThankYouPage] = useState("");
  const [thankYouGif, setThankYouGif] = useState("");
  const [isUploadingGif, setIsUploadingGif] = useState(false);
  const gifInputRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pointsPerCharm, setPointsPerCharm] = useState(MIN_POINTS_PER_CHARM);
  const [votingOpen, setVotingOpen] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(true);
  const [uploadCount, setUploadCount] = useState(MIN_UPLOAD_COUNT);
  const [savingYogaSettings, setSavingYogaSettings] = useState(false);
  const yogaSettingsHydratedRef = useRef(false);
  const [yogaPosesPageTitle, setYogaPosesPageTitle] = useState("");
  const [yogaImageUploadPageTitle, setYogaImageUploadPageTitle] = useState("");
  const [yogaSampleImg, setYogaSampleImg] = useState("");
  const [yogaSampleGif, setYogaSampleGif] = useState("");
  const [isUploadingYogaImg, setIsUploadingYogaImg] = useState(false);
  const [isUploadingYogaGif, setIsUploadingYogaGif] = useState(false);
  const yogaImgInputRef = useRef(null);
  const yogaGifInputRef = useRef(null);

  const [colors, setColors] = useState({
    text_color: "#000000",
    button_color: "#ffffff",
    button_backgroundcolor: "#000000",
    quiz_question_text_color: "#1a1a1a",
    quiz_question_background_color: "#ffffff",
    quiz_option_text_color: "#0d1b5c",
    quiz_option_background_color: "#eceff1",
    rules_text_color: "#333333",
  });

  useEffect(() => {
    if (!data) return;
    const c = data?.colors;
    const flat = data && !c && (data.text_color != null || data.button_color != null);
    const source = c || (flat ? data : null);
    setColors((prev) => ({
      text_color: (source?.text_color ?? data.text_color) ?? prev.text_color,
      button_color: (source?.button_color ?? data.button_color) ?? prev.button_color,
      button_backgroundcolor:
        (source?.button_backgroundcolor ?? data.button_backgroundcolor) ?? prev.button_backgroundcolor,
      quiz_question_text_color:
        ("quiz_question_text_color" in data
          ? data.quiz_question_text_color
          : prev.quiz_question_text_color) ?? prev.quiz_question_text_color,
      quiz_question_background_color:
        ("quiz_question_background_color" in data
          ? data.quiz_question_background_color
          : prev.quiz_question_background_color) ?? prev.quiz_question_background_color,
      quiz_option_text_color:
        ("quiz_option_text_color" in data ? data.quiz_option_text_color : prev.quiz_option_text_color) ??
        prev.quiz_option_text_color,
      quiz_option_background_color:
        ("quiz_option_background_color" in data
          ? data.quiz_option_background_color
          : prev.quiz_option_background_color) ?? prev.quiz_option_background_color,
      rules_text_color:
        data.rules_text_color ??
        source?.text_color ??
        data.text_color ??
        prev.rules_text_color,
    }));
  }, [data]);

  const handleColorChange = (key) => (e) => {
    setColors((prev) => ({ ...prev, [key]: e.target.value }));
  };

  // fetch questions & theme on mount
  useEffect(() => {
    yogaSettingsHydratedRef.current = false;
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    setRules(data.rules || [""]);
    setPointsPerCharm(clampPointsPerCharm(data.points_per_charm ?? data.points));
    // Only overwrite when the key exists on the payload. If fetchThemeData returns a
    // partial object (missing keys), do not use undefined → "" or we wipe a filename
    // just set by upload and abort the in-flight S3 image (NS_BINDING_ABORTED).
    if ("landing_page_title" in data) {
      const t = String(data.landing_page_title ?? "");
      setLandingPageTitle(t.slice(0, LANDING_TITLE_MAX_LEN));
    }
    if ("custom_text_thank_you_page" in data) {
      setThankYouPage(data.custom_text_thank_you_page ?? "");
    }
    if ("thank_you_page" in data) setThankYouGif(data.thank_you_page ?? "");
    if ("yoga_poses_page_title" in data) {
      setYogaPosesPageTitle(
        String(data.yoga_poses_page_title ?? "").slice(0, YOGA_POSES_TITLE_MAX_LEN)
      );
    }
    if ("yoga_image_upload_page_title" in data) {
      setYogaImageUploadPageTitle(
        String(data.yoga_image_upload_page_title ?? "").slice(0, YOGA_UPLOAD_TITLE_MAX_LEN)
      );
    }
    if ("yoga_sample_img" in data) setYogaSampleImg(data.yoga_sample_img ?? "");
    if ("yoga_sample_gif" in data) setYogaSampleGif(data.yoga_sample_gif ?? "");
    if ("voting_status" in data) setVotingOpen(parseStatusOpen(data.voting_status, true));
    if ("upload_status" in data) setUploadOpen(parseStatusOpen(data.upload_status, true));
    if ("upload_count" in data) setUploadCount(clampUploadCount(data.upload_count));
    yogaSettingsHydratedRef.current = true;
  }, [data]);

  const buildYogaSettingsData = useCallback(
    (overrides = {}) => {
      const vOpen = overrides.votingOpen ?? votingOpen;
      const uOpen = overrides.uploadOpen ?? uploadOpen;
      const uCount = overrides.uploadCount ?? uploadCount;
      return {
        voting_status: vOpen ? "open" : "closed",
        upload_status: uOpen ? "open" : "closed",
        upload_count: uOpen ? clampUploadCount(uCount) : MIN_UPLOAD_COUNT,
      };
    },
    [votingOpen, uploadOpen, uploadCount]
  );

  const persistYogaSettings = useCallback(
    async (overrides = {}) => {
      if (!currentTheme || !adminToken) {
        Swal.fire("No theme", "Select a theme before changing yoga settings.", "warning");
        return false;
      }
      setSavingYogaSettings(true);
      try {
        await updateThemeData({
          payload: {
            data: buildYogaSettingsData(overrides),
            currentTheme,
          },
          token: adminToken,
        });
        await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
        return true;
      } catch (e) {
        Swal.fire("Error", e.message || "Could not save yoga settings.", "error");
        await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
        return false;
      } finally {
        setSavingYogaSettings(false);
      }
    },
    [adminToken, buildYogaSettingsData, currentTheme, dispatch]
  );

  const handleVotingChange = async (checked) => {
    setVotingOpen(checked);
    if (!yogaSettingsHydratedRef.current) return;
    await persistYogaSettings({ votingOpen: checked });
  };

  const handleUploadChange = async (checked) => {
    setUploadOpen(checked);
    if (!yogaSettingsHydratedRef.current) return;
    await persistYogaSettings({ uploadOpen: checked });
  };

  const handleUploadCountChange = (value) => {
    if (value === "") {
      setUploadCount("");
      return;
    }
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed)) return;
    setUploadCount(parsed);
  };

  const handleUploadCountBlur = async () => {
    if (!uploadOpen || !yogaSettingsHydratedRef.current) return;
    if (uploadCount === "") {
      setUploadCount(MIN_UPLOAD_COUNT);
      await persistYogaSettings({ uploadCount: MIN_UPLOAD_COUNT });
      return;
    }
    const clamped = clampUploadCount(uploadCount);
    setUploadCount(clamped);
    await persistYogaSettings({ uploadCount: clamped });
  };

  // --- rules handlers ---
  const handleRuleChange = (idx, val) => {
    let newVal = val;
    if (val.length > 200) {
      Swal.fire(
        "Limit Exceeded",
        "Rules can be at most 200 characters. Extra text has been trimmed.",
        "warning"
      );
      newVal = val.slice(0, 200);
    }
    const arr = [...rules];
    arr[idx] = newVal;
    setRules(arr);
  };

  const handleAddRule = () => {
    if (rules.length >= 7) {
      return Swal.fire(
        "Limit Reached",
        "You can only add up to 7 rules.",
        "warning"
      );
    }
    setRules([...rules, ""]);
  };
  const handleRemoveRule = (idx) => {
    if (rules.length <= 1) {
      return Swal.fire(
        "At Least One Rule Required",
        "You must have at least one rule.",
        "warning"
      );
    }
    setRules(rules.filter((_, i) => i !== idx));
  };

  const S3 = process.env.REACT_APP_S3_PATH || "";

  const uploadYogaSample = async (
    e,
    { target, setFilename, setUploading, inputRef, allowedExt, label }
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!(await validateYogaSampleFile(file, { allowedExt, label }))) {
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("target", target);
    fd.append("themeName", String(currentTheme));

    try {
      const res = await fetch(apiUrl("/uploadSpotImage"), {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: fd,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Upload failed");

      setFilename(json.filename || "");
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Uploaded", `${label} updated.`, "success");
    } catch (err) {
      Swal.fire("Error", err.message || "Upload failed.", "error");
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleGifUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "gif") {
      Swal.fire("Invalid file", "Only GIF files are allowed.", "error");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire("File too large", "Maximum 5 MB.", "error");
      return;
    }

    setIsUploadingGif(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("target", "thank_you_page");
    fd.append("themeName", String(currentTheme));

    try {
      const res = await fetch(apiUrl("/uploadSpotImage"), {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: fd,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Upload failed");

      setThankYouGif(json.filename || "");
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Uploaded", "Thank you GIF updated.", "success");
    } catch (err) {
      Swal.fire("Error", err.message || "Upload failed.", "error");
    }
    setIsUploadingGif(false);
    if (gifInputRef.current) gifInputRef.current.value = "";
  };

  const handleLandingPageTitleChange = (val) => {
    let next = val;
    if (next.length > LANDING_TITLE_MAX_LEN) {
      Swal.fire(
        "Limit Exceeded",
        `Game page title can be at most ${LANDING_TITLE_MAX_LEN} characters. Extra text has been trimmed.`,
        "warning"
      );
      next = next.slice(0, LANDING_TITLE_MAX_LEN);
    }
    setLandingPageTitle(next);
  };

  const handleThankYouPageChange = (val) => {
    if (val.length > 300) {
      Swal.fire(
        "Limit Exceeded",
        "Thank you page message can be at most 300 characters. Extra text has been trimmed.",
        "warning"
      );
      val = val.slice(0, 300);
    }
    setThankYouPage(val);
  };

  const handlePointsPerCharmChange = (value) => {
    if (value === "") {
      setPointsPerCharm("");
      return;
    }
    const parsed = Number.parseInt(value, 10);
    if (!Number.isFinite(parsed)) return;
    setPointsPerCharm(parsed);
  };

  const validatePointsPerCharm = (rawValue, showPopup = true) => {
    const numeric = Number(rawValue);
    const isValid =
      Number.isFinite(numeric) &&
      numeric >= MIN_POINTS_PER_CHARM &&
      numeric <= MAX_POINTS_PER_CHARM;
    if (!isValid && showPopup) {
      Swal.fire(
        "Invalid game points",
        `Points per Charm must be between ${MIN_POINTS_PER_CHARM} and ${MAX_POINTS_PER_CHARM}.`,
        "warning"
      );
    }
    return isValid;
  };

  const handleYogaPosesPageTitleChange = (val) => {
    let next = val;
    if (next.length > YOGA_POSES_TITLE_MAX_LEN) {
      Swal.fire(
        "Limit Exceeded",
        `Yoga poses page title can be at most ${YOGA_POSES_TITLE_MAX_LEN} characters. Extra text has been trimmed.`,
        "warning"
      );
      next = next.slice(0, YOGA_POSES_TITLE_MAX_LEN);
    }
    setYogaPosesPageTitle(next);
  };

  const handleYogaImageUploadPageTitleChange = (val) => {
    let next = val;
    if (next.length > YOGA_UPLOAD_TITLE_MAX_LEN) {
      Swal.fire(
        "Limit Exceeded",
        `Yoga image upload page title can be at most ${YOGA_UPLOAD_TITLE_MAX_LEN} characters. Extra text has been trimmed.`,
        "warning"
      );
      next = next.slice(0, YOGA_UPLOAD_TITLE_MAX_LEN);
    }
    setYogaImageUploadPageTitle(next);
  };

  const handlePointsPerCharmBlur = () => {
    if (pointsPerCharm === "") {
      validatePointsPerCharm(pointsPerCharm, true);
      setPointsPerCharm(MIN_POINTS_PER_CHARM);
      return;
    }
    const valid = validatePointsPerCharm(pointsPerCharm, true);
    if (!valid) {
      setPointsPerCharm(clampPointsPerCharm(pointsPerCharm));
    }
  };

  // --- submit all ---
  const handleSubmit = async () => {
    if (String(landingPageTitle ?? "").length > LANDING_TITLE_MAX_LEN) {
      Swal.fire(
        "Invalid title",
        `Game page title must be at most ${LANDING_TITLE_MAX_LEN} characters.`,
        "warning"
      );
      return;
    }
    const trimmedRules = rules.map((r) => String(r ?? "").trim());
    if (trimmedRules.some((r) => r === "")) {
      Swal.fire(
        "Incomplete rules",
        "Please add text to every rule, or remove empty rows using the × button before saving.",
        "warning"
      );
      return;
    }
    if (trimmedRules.length === 0) {
      Swal.fire(
        "Rules required",
        "Please add at least one rule with text before saving.",
        "warning"
      );
      return;
    }
    const normalizedPointsPerCharm = clampPointsPerCharm(pointsPerCharm);
    if (!validatePointsPerCharm(pointsPerCharm, true)) {
      return;
    }

    const payload = {
      data: {
        rules: trimmedRules,
        landing_page_title: String(landingPageTitle ?? "").slice(0, LANDING_TITLE_MAX_LEN),
        custom_text_thank_you_page: thankYouPage,
        text_color: colors.text_color,
        button_color: colors.button_color,
        button_backgroundcolor: colors.button_backgroundcolor,
        quiz_question_text_color: colors.quiz_question_text_color,
        quiz_question_background_color: colors.quiz_question_background_color,
        quiz_option_text_color: colors.quiz_option_text_color,
        quiz_option_background_color: colors.quiz_option_background_color,
        rules_text_color: colors.rules_text_color,
        points_per_charm: normalizedPointsPerCharm,
        points: normalizedPointsPerCharm,
        voting_status: votingOpen ? "open" : "closed",
        upload_status: uploadOpen ? "open" : "closed",
        upload_count: uploadOpen ? clampUploadCount(uploadCount) : MIN_UPLOAD_COUNT,
        yoga_poses_page_title: String(yogaPosesPageTitle ?? "").slice(
          0,
          YOGA_POSES_TITLE_MAX_LEN
        ),
        yoga_image_upload_page_title: String(yogaImageUploadPageTitle ?? "").slice(
          0,
          YOGA_UPLOAD_TITLE_MAX_LEN
        ),
      },
      currentTheme,
    };

    try {
      await updateThemeData({ payload, token: adminToken });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      setTimeout(() => navigate("/admin/customize-spot"), 500);
    } catch (e) {
      Swal.fire(
        "Error",
        e.message || "Failed to save. Please try again.",
        "error"
      );
    }
  };

  return (
    <>
      <ColorPallet isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />


          <div className="back-button-holder">
              <button
                className="back-button"
                onClick={() => navigate("/admin/")}
              >
              <i className="fa-solid fa-arrow-left"></i> Back
              </button>
          </div>
      <div className="rules-page-new">
        <div className="admin-tabs-holder">
          <div className="rules-page-columns">
            {/* <div className="rules-page-left">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-pen"></i> &nbsp; Basic Theme Details
                </div>
                <div className="form-group-v1">
                  <label>Game page title</label>
                  <textarea
                    value={landingPageTitle}
                    onChange={(e) => handleLandingPageTitleChange(e.target.value)}
                    placeholder="Game page title"
                    className="form-control rules-textarea"
                    rows={2}
                    maxLength={LANDING_TITLE_MAX_LEN}
                  />
                  <div className="code">
                    * Maximum {LANDING_TITLE_MAX_LEN} characters.
                  </div>
                </div>

                <div className="form-group-v1">
                  <label>Thank you page message</label>
                  <textarea
                    value={thankYouPage}
                    onChange={(e) => handleThankYouPageChange(e.target.value)}
                    placeholder="Thank you page message"
                    className="form-control rules-textarea"
                    rows={3}
                    maxLength={300}
                  />
                  <div className="code">* Maximum 300 characters.</div>
                </div>

                <div className="form-group-v1">
                  <label>Thank You GIF </label>
                  {thankYouGif && (
                    <div className="thankyou-gif-preview">
                      <img
                        src={s3AssetUrl(S3, thankYouGif)}
                        alt="Thank you"
                        className="thankyou-gif-thumb"
                        referrerPolicy="no-referrer"
                      />
                    </div>
                  )}
                  <input
                    ref={gifInputRef}
                    type="file"
                    accept=".gif"
                    onChange={handleGifUpload}
                    disabled={isUploadingGif}
                    className="thankyou-gif-input"
                  />
                  {isUploadingGif && (
                    <div className="code">Uploading…</div>
                  )}
                  <div className="code">* Accepts GIF only. Max 5 MB.</div>
                </div>
              </div>
            </div> */}

            <div className="rules-page-center">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-trophy"></i> &nbsp; Game Rules
                </div>
                <div className="yoga-sample-uploads">
                  <h3 className="custom-rules-title">Yoga sample assets</h3>

                  <div className="form-group-v1">
                    <label>Yoga sample image (200×200)</label>
                    {yogaSampleImg && (
                      <div className="yoga-sample-preview">
                        <img
                          src={s3AssetUrl(S3, yogaSampleImg)}
                          alt="Yoga sample"
                          className="yoga-sample-thumb"
                          referrerPolicy="no-referrer"
                        />
                      </div>
                    )}
                    <input
                      ref={yogaImgInputRef}
                      type="file"
                      accept=".jpg,.jpeg,.png"
                      onChange={(e) =>
                        uploadYogaSample(e, {
                          target: "yoga_sample_img",
                          setFilename: setYogaSampleImg,
                          setUploading: setIsUploadingYogaImg,
                          inputRef: yogaImgInputRef,
                          allowedExt: new Set(["jpg", "jpeg", "png"]),
                          label: "Yoga sample image",
                        })
                      }
                      disabled={isUploadingYogaImg}
                      className="yoga-sample-input"
                    />
                    {isUploadingYogaImg && <div className="code">Uploading…</div>}
                    <div className="code">* JPG/PNG only. Exactly 200×200 px. Max 5 MB.</div>
                  </div>

                  <div className="form-group-v1">
                    <label>Yoga sample GIF (200×200)</label>
                    {yogaSampleGif && (
                      <div className="yoga-sample-preview">
                        <img
                          src={s3AssetUrl(S3, yogaSampleGif)}
                          alt="Yoga sample GIF"
                          className="yoga-sample-thumb"
                          referrerPolicy="no-referrer"
                        />
                      </div>
                    )}
                    <input
                      ref={yogaGifInputRef}
                      type="file"
                      accept=".gif"
                      onChange={(e) =>
                        uploadYogaSample(e, {
                          target: "yoga_sample_gif",
                          setFilename: setYogaSampleGif,
                          setUploading: setIsUploadingYogaGif,
                          inputRef: yogaGifInputRef,
                          allowedExt: new Set(["gif"]),
                          label: "Yoga sample GIF",
                        })
                      }
                      disabled={isUploadingYogaGif}
                      className="yoga-sample-input"
                    />
                    {isUploadingYogaGif && <div className="code">Uploading…</div>}
                    <div className="code">* GIF only. Exactly 200×200 px. Max 5 MB.</div>
                  </div>
                </div>

                <h3 className="custom-rules-title">Custom rules</h3>
                {rules.map((rule, i) => (
                  <div className="rule-row" key={i}>
                    <span className="rule-number">{i + 1}.</span>
                    <input
                      className="rule-input"
                      type="text"
                      placeholder="Enter rule"
                      value={rule}
                      onChange={(e) => handleRuleChange(i, e.target.value)}
                    />
                    <button
                      className="remove-btn"
                      onClick={() => handleRemoveRule(i)}
                    >
                      &times;
                    </button>
                  </div>
                ))}
                <div className="rules-button-container">
                  <div>
                    <button className="add-btn" onClick={handleAddRule}>
                      + Add New Rule
                    </button>
                  </div>
                  <div className="code">
                    * Keep 7 rules at the most. <br></br>* At least 1 rule is
                    required.<br></br>* Character limit of each rule is 200<br></br>
                  </div>
                </div>
              </div>
            </div>

            <div className="rules-page-right">
              <div className="admin-tab-container">
                <div className="admin-tab-title">
                  <i className="fa-solid fa-palette"></i> &nbsp; Customization
                </div>
                <div className="form-group-v1">
                  <label>Game Points</label>
                  <input
                    type="number"
                    min={MIN_POINTS_PER_CHARM}
                    max={MAX_POINTS_PER_CHARM}
                    value={pointsPerCharm}
                    onChange={(e) => handlePointsPerCharmChange(e.target.value)}
                    onBlur={handlePointsPerCharmBlur}
                  />
                  <div className="code">
                    {/* Points per Charm: {clampPointsPerCharm(pointsPerCharm)} <br /> */}
                    * Minimum {MIN_POINTS_PER_CHARM} points and maximum {MAX_POINTS_PER_CHARM} points.
                  </div>
                </div>
                <div className="form-group-v1 rules-toggle-row">
                  <label>Voting (gallery likes)</label>
                  <div className="rules-toggle-control">
                    <Switch
                      checked={votingOpen}
                      onChange={handleVotingChange}
                      disabled={savingYogaSettings}
                      onColor="#7c3aed"
                      offColor="#cbd5e1"
                      height={22}
                      width={44}
                    />
                    <span>
                      {savingYogaSettings ? "Saving…" : votingOpen ? "Open" : "Closed"}
                    </span>
                  </div>
                  <div className="code">When closed, like buttons are hidden on the yoga gallery.</div>
                </div>
                <div className="form-group-v1 rules-toggle-row">
                  <label>Player upload</label>
                  <div className="rules-toggle-control">
                    <Switch
                      checked={uploadOpen}
                      onChange={handleUploadChange}
                      disabled={savingYogaSettings}
                      onColor="#7c3aed"
                      offColor="#cbd5e1"
                      height={22}
                      width={44}
                    />
                    <span>
                      {savingYogaSettings ? "Saving…" : uploadOpen ? "Open" : "Closed"}
                    </span>
                  </div>
                  <div className="code">
                    When closed, players finish poses and go straight to the gallery.
                  </div>
                </div>
                {uploadOpen ? (
                  <div className="form-group-v1">
                    <label>Upload count per player</label>
                    <input
                      type="number"
                      min={MIN_UPLOAD_COUNT}
                      max={MAX_UPLOAD_COUNT}
                      value={uploadCount}
                      onChange={(e) => handleUploadCountChange(e.target.value)}
                      onBlur={handleUploadCountBlur}
                      disabled={savingYogaSettings}
                    />
                    <div className="code">
                      * How many photos/videos each player may upload (default 1, max {MAX_UPLOAD_COUNT}).
                    </div>
                  </div>
                ) : null}
                <div className="form-group-v1">
                  <button
                    type="button"
                    className="rules-manage-uploads-btn"
                    onClick={() => navigate("/admin/yoga-uploads")}
                  >
                    Manage gallery uploads
                  </button>
                </div>
                <div className="form-group-v1">
                  <label>Yoga poses page title</label>
                  <textarea
                    value={yogaPosesPageTitle}
                    onChange={(e) => handleYogaPosesPageTitleChange(e.target.value)}
                    placeholder="Yoga Quest"
                    className="form-control rules-textarea"
                    rows={3}
                    maxLength={YOGA_POSES_TITLE_MAX_LEN}
                  />
                  <div className="code">
                    * Maximum {YOGA_POSES_TITLE_MAX_LEN} characters.
                  </div>
                </div>
                <div className="form-group-v1">
                  <label>Yoga image upload page title</label>
                  <textarea
                    value={yogaImageUploadPageTitle}
                    onChange={(e) => handleYogaImageUploadPageTitleChange(e.target.value)}
                    placeholder="Players upload a photo or video of themselves performing any yoga pose"
                    className="form-control rules-textarea"
                    rows={3}
                    maxLength={YOGA_UPLOAD_TITLE_MAX_LEN}
                  />
                  <div className="code">
                    * Maximum {YOGA_UPLOAD_TITLE_MAX_LEN} characters.
                  </div>
                </div>
                <div className="pallet-holder">
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.text_color}
                          onChange={handleColorChange("text_color")}
                        />
                        <label>Text Color</label>
                      </div>
                      <div className="color-code">{colors.text_color}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.button_color}
                          onChange={handleColorChange("button_color")}
                        />
                        <label>Button Text Color</label>
                      </div>
                      <div className="color-code">{colors.button_color}</div>
                    </div>
                  </div>
                  <div className="pallet-row">
                    <div className="pallet-input">
                      <div className="color-holder">
                        <input
                          type="color"
                          className="pallet-swatch"
                          value={colors.button_backgroundcolor}
                          onChange={handleColorChange("button_backgroundcolor")}
                        />
                        <label>Button Background Color</label>
                      </div>
                      <div className="color-code">{colors.button_backgroundcolor}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="rules-action-holder">
            <button className="save-and-continue" onClick={handleSubmit}>
              Save & Continue
            </button>
           
          </div>
        </div>
      </div>
    </>
  );
};

export default Rules;
